import React, { useState } from "react";
import { leaveOn } from "../../context/ClinicDataContext";
import Modal from "../../components/Modal/Modal";
import { useClinicData } from "../../context/ClinicDataContext";
import { dayName, formatDateShort, formatTime } from "../../utils/format";

// Deactivating keeps the dentist's history but removes them from booking.
// Any remaining appointments must move to a colleague first.
const DeactivateModal = ({ dentist, onClose }) => {
  const { activeDentists, openAppointmentsFor, reassignAppointments, setDentistActive, patientById, findClash, dentistById } =
    useClinicData();
  const remaining = openAppointmentsFor(dentist.id);
  const colleagues = activeDentists.filter((d) => d.id !== dentist.id);
  const [target, setTarget] = useState(colleagues[0]?.id || "");

  const blocked = remaining.length > 0 && !colleagues.length;

  // Visits that won't fit the chosen colleague: double-booked, on leave, or not a working day for them.
  const conflicts = target
    ? remaining
        .map((a) => {
          const to = dentistById[target];
          if (findClash({ ...a, dentistId: target, ignoreId: a.id })) return { a, reason: "already booked" };
          if (leaveOn(to, a.date)) return { a, reason: "on leave" };
          const day = dayName(a.date);
          if (!to.days.includes(day)) return { a, reason: `doesn't work ${day}` };
          return null;
        })
        .filter(Boolean)
    : [];

  const handleConfirm = () => {
    if (remaining.length) reassignAppointments(dentist.id, target);
    setDentistActive(dentist.id, false);
    onClose();
  };

  return (
    <Modal
      title={`Deactivate ${dentist.name}?`}
      onClose={onClose}
      width={560}
      footer={
        <>
          <button className="btn btn--secondary" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn--primary btn--destructive" type="button" onClick={handleConfirm} disabled={blocked}>
            {remaining.length ? "Move appointments & deactivate" : "Deactivate"}
          </button>
        </>
      }
    >
      <p className="modal__lead">
        {dentist.name} will be hidden from the patient portal and can't be booked. Past visits stay on patient records, and you can
        reactivate them at any time.
      </p>

      {remaining.length > 0 && (
        <>
          <div className="appt-form__notice">
            {remaining.length} upcoming appointment{remaining.length > 1 ? "s" : ""} still booked:
            <ul className="deactivate__list">
              {remaining.slice(0, 5).map((a) => (
                <li key={a.id}>
                  {formatDateShort(a.date)}, {formatTime(a.start)} · {patientById[a.patientId]?.name}
                </li>
              ))}
              {remaining.length > 5 && <li>and {remaining.length - 5} more</li>}
            </ul>
          </div>
          {colleagues.length ? (
            <label className="form-field">
              <span className="form-field__label">Move them to</span>
              <select className="form-field__control" value={target} onChange={(e) => setTarget(e.target.value)}>
                {colleagues.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} ({d.role})
                  </option>
                ))}
              </select>
              <span className="form-field__hint">Let the patients know about the change of dentist.</span>
            </label>
          ) : null}
          {colleagues.length > 0 && conflicts.length > 0 ? (
            <div className="form-error deactivate__conflicts">
              {conflicts.length} of these won't fit {dentistById[target]?.name}'s diary. They'll still move, so reschedule them
              afterwards:
              <ul className="deactivate__list">
                {conflicts.map(({ a, reason }) => (
                  <li key={a.id}>
                    {formatDateShort(a.date)}, {formatTime(a.start)} · {patientById[a.patientId]?.name} ({reason})
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {colleagues.length ? null : (
            <p className="form-error">Add another dentist first, so these appointments have someone to move to.</p>
          )}
        </>
      )}
    </Modal>
  );
};

export default DeactivateModal;
